import { getStage } from 'core/components/reels/stages/FactoryStage'
import { BasePool } from 'core/components/reels/symbols/base/BasePool'
import { ReelSymbol } from 'core/components/reels/symbols/base/ReelSymbol'
import { gsap } from 'gsap'
import { ChanceManager } from 'managers/ChanceManager'
import { signal } from 'services/SignalsService'
import { StoreageService } from 'services/StoreageService'
import { makeSymbolsService } from 'services/SymbolsService'
import { reelsSettings, spinSettings } from 'settings/custom/reels'
import { delayCallBack } from 'utils/Delay'

export default class BaseReel extends PIXI.Container {
  constructor(data) {
    super()
    const spinModel = game.models.SpinModel
    this._time = 0
    this.canStop = false
    this.reelItems = []
    this.reelPosition = 0
    this.spinContainer = new PIXI.Container()
    this.spinStickyContainer = new PIXI.Container()
    this.spinStickySymbols = []
    this.stickyPosition = []
    this.symbolPool = new BasePool()
    this.id = parseFloat(data.name.match(/\d+/))
    this.currentStage = null
    this.itemCount = reelsSettings.itemCount[this.id]
    this.updateWin = false
    this.swapItems = []
    this.symbols = makeSymbolsService(this.id)
    this.matrix =  spinModel.get().matrix.length > 0 ? game.models.SpinModel.get().matrix[this.id].slice() : []
    this.settings = spinSettings[game.models.ModeModel.turbo]
    this.addChild(this.spinContainer)
    this.addChild(this.spinStickyContainer)
    this.createSignal()
  }

  init() {
    this.initSymbols()
    this.initTimeStages()
    this.move(false)
  }

  makeStickySymbol(pos){
    const checkSticky = this.stickyPosition.includes(pos)
    if(!checkSticky){
      const itemHeight = game.models.ItemsModel.height
      const item = new ReelSymbol(this.symbolPool)

      item.isSticky = true
      item.setSymbol(reelsSettings.stickyWild)
      item.positionY = pos
      item.y = (pos  * itemHeight )
      item.animationSticky()

      this.reelItems[pos].visible = false
      this.stickyPosition.push(pos)
      this.spinStickyContainer.addChild(item)
      this.spinStickySymbols.push(item)

      game.models.SpinModel.setSymbol(this.id, pos, item)
      signal.dispatch('SOUND:STICKY-START')
    }
    else {
      this.spinStickySymbols.forEach((item) => {
        if(item.positionY === pos){
          this.reelItems[pos].visible = false
          game.models.SpinModel.setSymbol(this.id, pos, item)
        }

      })
    }
  }

  stopWithSticky(){
    this.spinStickySymbols.forEach((item) => {
      game.models.SpinModel.setSymbol(this.id, item.positionY, item)
    })
  }

  clearSticky(){
    this.clearSwapSymbol()
    this.spinStickySymbols.forEach((item) => {
      item.destroy()
    })
    this.spinStickySymbols = []
    this.stickyPosition = []
    game.models.FreespinModel.stickyPosition = []
  }

  initSymbols(){
    const itemHeight = game.models.ItemsModel.height
    for (let i = -this.itemCount; i < this.itemCount  + 2; i++) {
      const item = new ReelSymbol(this.symbolPool)
      spinSettings.debug ? this.createTextDebug(item, i) : () => {}
      item.setSymbol(this.symbols.get())
      item.positionY = i
      item.y = (i - this.itemCount) * itemHeight
      item.pos = i
      this.reelItems.push(item)
      this.spinContainer.addChild(item)
      game.models.SpinModel.setSymbol(this.id, i, item)
    }

    this.spinContainer.y = -(this.itemCount) * itemHeight
    this.reelPosition = this.itemCount + 1
    this.updateSymbolPosition(true)
    this.resetSymbols()
    this.updateWin = true
    this.updateSymbolWin()
  }

  initTimeStages(){
    this.stages = []
    this.turboStages = []
    this.stageSetting = spinSettings.spin
    this.stageTurboSetting = spinSettings.turbo

    this.stageSetting.forEach((stage, index) => {
      const timeStage = getStage(stage, index)
      timeStage.delay = timeStage.name === 'start' ?  this.stageSetting[index].delay * this.id : this.stageSetting[index].delay ? this.stageSetting[index].delay : 0
      timeStage.reelID = this.id
      this.stages.push(timeStage)
    })

    this.stageTurboSetting.forEach((stage, index) => {
      const timeStage = getStage(stage, index)
      timeStage.delay = timeStage.name === 'start' ?  this.stageTurboSetting[index].delay * this.id : this.stageTurboSetting[index].delay ? this.stageTurboSetting[index].delay : 0
      timeStage.reelID = this.id
      this.turboStages.push(timeStage)
    })

  }

  createSignal() {
    signal.once('STATE:OUT:INIT', this.init.bind(this))
    signal.on('SPREAD-SYMBOL', this.spreading.bind(this))
    signal.on('STICKY-SYMBOL', this.stickSymbol.bind(this))
    signal.on('STATE:IN:FS-END', this.clearSticky.bind(this))
    signal.on('START-STOP-ANIMATION', (id) => {
      this.move(false)
      if(reelsSettings.items.states['stop']){
        if(id === this.id) {
          this.stopWithSticky()
          this.reelItems.forEach((el) => el.animationStop(el))
        }

      }
    })

    signal.on('REELS:UPDATE', () => {
      this.updateSymbolWin()
      this.resetSymbols()
    })

    signal.on('END-STOP-REEL', (id) => {
      if(id === this.id) {
        this.spinEnd()
        signal.dispatch(`SPIN-END-${id}`)
      }
    })
  }

  resetSymbols() {
    for (let i = 0; i < this.reelItems.length; i++) {
      this.reelItems[i].resetSymbol()
      if(spinSettings.debug){
        this.reelItems[i].debugText.text = i
      }

    }
  }

  resetSpin(){
    this._time = 0
    this.updateWin = false
    this.canStop = false
    this.clearSwapSymbol()

    this.reelPosition =  this.itemCount + 1
    this.updateSymbolPosition(true)
    this.currentStage = StoreageService.get('turbo') ? this.turboStages[0] : this.stages[0]
    this.currentStage.startPosition = this.reelPosition
    this.move(true)
  }

  update(){
    if(this.spinning){
      const ticker = game.application.ticker
      let dt = (gsap.ticker.deltaRatio(ticker.FPS)  / (ticker.FPS / gsap.globalTimeline.timeScale()))
      let stage = this.currentStage
      let calculatePosition = true
      let position = this.reelPosition

      this._time += dt

      while (calculatePosition) {
        position = this.reelPosition = stage.updatePosition(this.reelPosition, this._time, dt)
        this.reelPosition = position
        this.updateSymbolPosition()
        if(stage.name === 'spin' && stage.isComplete && !this.canStop){
          this.reelPosition = stage.startPosition
          stage.isComplete = false
        }

        if (stage.isComplete) {
          stage.reset()
          this._time = 0
          if(stage.updateWinReel){
            this.updateSymbolWin()
            this.updateWin = true
          }

          stage = this.currentStage = this.nextStage(stage.id)
          if (stage != null) {
            stage.start(this.reelPosition, 0)
          }
          else {
            this.spinning = false
            calculatePosition = false
          }
        }
        else {
          calculatePosition = false
        }
      }

    }
  }

  nextStage(id){
    return    StoreageService.get('turbo') ? this.turboStages[id + 1] : this.stages[id + 1]
  }

  updateSymbolWin(){
    let symbolCount = 0
    this.matrix = game.models.SpinModel.get().matrix[this.id].slice()
    this.matrix.reverse()
    for (let i = 0; i <  this.itemCount; i++) {
      this.reelItems[i].visible = true
      this.reelItems[i].setSymbol(this.matrix.pop())
      this.spinStickySymbols.forEach((item) => {
        if(item.positionY === symbolCount) {
          this.reelItems[i].swapSticky(true)
          this.swapItems.push(this.reelItems[i])
        }
      })

      this.reelItems[i].playMove()
      game.models.SpinModel.setSymbol(this.id, symbolCount++, this.reelItems[i])
    }

  }

  clearSwapSymbol(){
    this.swapItems.forEach((item) => {
      item.visible = true
      item.swapSticky(false)
    })
    this.swapItems = []
  }

  updateSymbolPosition(){
    const itemHeight = game.models.ItemsModel.height
    const reelItem =  this.reelItems.length

    for (let j = 0; j < reelItem; j++) {
      const s = this.reelItems[j]
      const prevY = s.y
      s.y = ((this.reelPosition + j) % reelItem) * itemHeight - itemHeight

      if (s.y < 0 && prevY > itemHeight) {
        if(!this.updateWin ){
          s.setSymbol(this.symbols.get())
        }
      }
    }
  }

  async spin() {
    this.resetSpin()
    this.setReelToUpdate()
    this.spinning = true

    return signal.promise( `SPIN-END-${this.id}`)

  }

  spinEnd(){
    this.move(false)
    this.resetSymbols()
    this.clearReelToUpdate()
  }

  async stop() {
    await ChanceManager.play(this.id)
    this.canStop = true
  }

  move(v) {
    this.reelItems.forEach((el) => el.setType(v ? 'move' : false))
  }

  spreading([reel, pos]) {
    this.id === reel && this.reelItems[pos].setSymbol(game.models.FreespinModel.get().expendingSymbol)
  }

  stickSymbol([reel, pos]) {
    this.id === reel && this.makeStickySymbol(pos)
    delayCallBack(1, () => {
      signal.dispatch('STICKY-SYMBOL:DONE')
    }).then(null)

  }

  async stick() {
    return signal.promise( `STICK-END-${this.id}`)
  }

  refresh(isFs) {
    this.matrix.push(...game.models.SpinModel.get().matrix[this.id].slice())
    this.reelItems.forEach((el, pos) => {
      el.pos = pos
      el.update(isFs)
    })
    this.move(false)

  }

  createTextDebug(item, i){
    const style = new PIXI.TextStyle({
      fill: 'gray',
      fontSize: 35,
      stroke: 'white',
      strokeThickness: 2,
    })
    const text = new PIXI.Text(`${i}`, style)
    text.x = 50
    text.y = 50
    item.addChild(text)
    item.debugText = text
  }

  setReelToUpdate(){
    game.application.ticker.add(this.update, this)
  }

  clearReelToUpdate(){
    game.application.ticker.remove(this.update, this)
  }
}
